import { Message } from '@/features/messages/messages.types';
import {
  ConversationUser,
  ConversationAgent,
  SenderDetails,
} from '../conversations.types';

/**
 * Determines sender details based on message properties and available agents
 * Handles user messages, AI agent messages, and human agent messages
 */
export const getSenderDetails = (
  message: Message,
  user: ConversationUser | null,
  aiAgent: ConversationAgent,
  humanAgent: ConversationAgent,
): SenderDetails => {
  // Default fallback if user is null
  if (!user) {
    return {
      isUser: false,
      avatar: '/default-avatar.png',
      name: 'User',
      isAIAgent: false,
    };
  }

  // AI agent messages
  if (message.from_agent === true) {
    return {
      isUser: false,
      avatar: aiAgent.avatar,
      name: message.sender_name || 'AI Assistant',
      isAIAgent: true,
    };
  }

  // Human agent messages (explicitly marked as from_agent = false)
  if (message.sender_id === humanAgent.id && message.from_agent === false) {
    return {
      isUser: false,
      avatar: humanAgent.avatar,
      name: humanAgent.name || message.sender_name || 'Human Agent',
      isAIAgent: false,
    };
  }
  // Outgoing messages TO the user/customer (sent from inbox)
  // Frontend sets: sender_id = currentUser.id, receiver_id = user.id
  if (message.receiver_id === user.id && message.sender_id !== user.id) {
    return {
      isUser: true, // Show on right side (blue) - outgoing from inbox
      avatar: humanAgent.avatar || '/default-agent-avatar.png',
      name: message.sender_name || humanAgent.name || 'You',
      isAIAgent: false,
    };
  }

  // Incoming messages FROM the user/customer (WhatsApp contact)
  // Frontend sets: sender_id = user.id, receiver_id = null
  if (message.sender_id === user.id) {
    const displayName = user.first_name
      ? `${user.first_name} ${user.last_name || ''}`.trim()
      : user.email || message.sender_name || 'Customer';

    return {
      isUser: true, // isUser=true means LEFT side in the component (flex-start)
      avatar: user.avatar || user.profile_image || '/default-user-avatar.png',
      name: displayName,
      isAIAgent: false,
    };
  }

  // Default to human agent if nothing else matches
  return {
    isUser: true,
    avatar: humanAgent.avatar,
    name: message.sender_name || 'User',
    isAIAgent: false,
  };
};
